import React from 'react';
import { CopyOutlined, EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import HotelPmsIDInput from './HotelPmsIDInput/HotelPmsIDInput';
import IPWhitelistInput from './IPWhitelistInput/IPWhitelistInput';
import styles from './PaperlessPush.styles';
import { Tooltip, Select, Button } from 'antd';
import Colors from 'constants/Colors';

function getPaperlessPushColumns({
  actions,
  translate,
  setPMSId,
  setIpWhitelist,
  isValidIpList,
  getExampleLink,
  goToAccountDetail,
  paperlessDevices,
  appState,
  getApiPaperlessCrendetials,
  apiPaperlessStatus,
}) {
  return [
    {
      title: translate('hotel'),
      dataIndex: 'hotel',
      key: 'hotel',
      searchable: true,
      sorter: false,
      render: (hotel, item) => {
        return (
          <div
            style={{
              ...styles.rowStyle,
              color: isEnabled(item) ? Colors.darkText : Colors.grayText,
            }}
          >
            {hotel}
          </div>
        );
      },
    },
    {
      title: translate('paperlessPush.pmsId'),
      dataIndex: 'pmsId',
      key: 'pmsId',
      render: (_, item) => {
        return (
          <div style={styles.rowStyle}>
            <HotelPmsIDInput
              disabled={!isEnabled(item)}
              key={item.hotelId}
              item={item}
              setPMSId={setPMSId}
            />
          </div>
        );
      },
    },

    // Only show this table for superUsers or non guest
    ...(['admin', 'owner'].includes(appState.role) || appState.superUser
      ? [
          {
            title: translate('paperlessPush.devicesTitle'),
            dataIndex: 'devices',
            key: 'devices',
            render: (_, item) => {
              let selectState = true;
              let filteredItems = [];

              if (paperlessDevices.length > 0) {
                filteredItems = paperlessDevices.filter(device => device.hotelId === item.hotelId);

                if (filteredItems.length === 0) selectState = false;
              } else {
                selectState = false;
              }

              return (
                <div style={{ ...styles.rowStyle }}>
                  <Select
                    suffixIcon={null}
                    style={{ width: '95%' }}
                    disabled={!selectState}
                    placeholder={
                      !selectState
                        ? translate('paperlessPush.selectDevicesEmpty')
                        : translate('paperlessPush.selectDevice')
                    }
                    dropdownRender={() => (
                      <div>
                        {filteredItems.map(device => (
                          <div
                            key={device.uuid}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              cursor: 'pointer',
                            }}
                          >
                            <span>{device.uuid}</span>

                            <div style={{ display: 'flex', gap: '4px' }}>
                              <Button
                                size="small"
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  actions.onEditDeviceClicked(device.uuid, device);
                                }}
                              />
                              <Button
                                size="small"
                                type="link"
                                icon={<DeleteOutlined />}
                                onClick={() => actions.onDeleteDeviceClicked(device.uuid)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </div>
              );
            },
          },
        ]
      : []),

    {
      title: translate('paperlessPush.ipWhitelist'),
      dataIndex: 'ipWhitelist',
      key: 'ipWhitelist',
      render: (_, item) => {
        return (
          <div style={styles.rowStyle}>
            <IPWhitelistInput
              disabled={!isEnabled(item)}
              key={item.hotelId}
              item={item}
              setIpWhitelist={setIpWhitelist}
              isValidIpList={isValidIpList}
            />
          </div>
        );
      },
    },
    {
      title: translate('paperlessPush.apiKey'),
      dataIndex: 'apiKey',
      key: 'apiKey',
      align: 'center',
      render: (apiKey, item) => {
        return isEnabled(item) ? (
          <div style={styles.rowStyle}>
            <div style={styles.btnContainer}>
              <div style={{ alignSelf: 'center' }}>
                <AppButton
                  disabled={!isEnabled(item)}
                  action={() => navigator.clipboard.writeText(apiKey)}
                  secondayIcon={<CopyOutlined style={styles.copyIcon} />}
                  secondary
                />
              </div>
            </div>
          </div>
        ) : null;
      },
    },
    {
      title: translate('paperlessPush.example'),
      dataIndex: 'hotel',
      key: 'hotel',
      align: 'center',
      render: (_, item) => {
        return (
          <div style={styles.rowStyle}>
            <div style={styles.btnContainer}>
              <Tooltip
                title={getExampleLink(item)}
                trigger={isEnabled(item) ? 'click' : ''}
                color={Colors.disableButton}
                key={Colors.disableButton}
              >
                <div>
                  {isEnabled(item) ? (
                    <AppButton secondayIcon={<EyeOutlined style={styles.exampleIcon} />} />
                  ) : (
                    <AppButton
                      text={translate('paperlessPush.activateSuscription')}
                      secondary
                      action={goToAccountDetail}
                    />
                  )}
                </div>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    ...(appState?.email.includes('@civitfun') || appState.superUsererUser
      ? [
          {
            title: translate('paperlessPush.activeApiPaperlessTitle'),
            dataIndex: 'apiPaperless',
            key: 'apiPaperless',
            align: 'center',
            render: (_, item) => {
              return (
                <div style={styles.rowStyle}>
                  <div style={styles.btnContainer}>
                    {isEnabled(item) ? (
                      <AppButton
                        text={translate(
                          item.hasCredentials
                            ? 'paperlessPush.activatedText'
                            : 'paperlessPush.activateText'
                        )}
                        action={() => getApiPaperlessCrendetials(item)}
                        disabled={item.hasCredentials}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
  ];
}

function isEnabled(item) {
  return item?.suscription?.active === 1;
}

export default { getPaperlessPushColumns };
